
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';
import { NavigationLinks } from './header/NavigationLinks';
import { CompactLanguageSelector } from './header/CompactLanguageSelector';
import { AuthButtons } from './header/AuthButtons';
import { MobileMenu } from './header/MobileMenu';
import ModernLogo from '@/components/shared/ModernLogo';
import { CityLanguageLinks } from '@/components/seo/CityLanguageLinks';
import { extractLanguageFromPath, extractCitySlug } from '@/utils/routing/multilingualRouting';
import { detectCityLanguage } from '@/utils/cityLanguageDetector';
import { CityLanguage } from '@/context/languages/languageData';
import { useCityData } from '@/hooks/useCityData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { getLocalizedPath } = useLanguage();
  const location = useLocation();

  // Usar Supabase como fuente única para ciudades
  const citySlug = extractCitySlug(location.pathname);
  const { data: cityInfo } = useCityData(citySlug, { fallbackToStatic: true });
  
  // Determinar el idioma actual de la ciudad
  const urlLanguage = extractLanguageFromPath(location.pathname) || 'en';
  const localLang = cityInfo ? detectCityLanguage(cityInfo) : 'en';
  const validLanguages = localLang === 'en' ? ['en'] : ['en', localLang];
  const cityLanguage = validLanguages.includes(urlLanguage as CityLanguage) 
    ? (urlLanguage as CityLanguage) 
    : 'en';
  
  // Solo mostrar selector de idioma si la ciudad tiene idioma local diferente de inglés
  const showCityLanguageLinks = cityInfo && localLang !== 'en';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside or pressing escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out',
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        )}
      >
        <div className="container mx-auto px-4 lg:px-6 xl:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <Link to={getLocalizedPath('/')} className="flex items-center flex-shrink-0">
              <ModernLogo />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex">
              <NavigationLinks />
            </div>

            {/* Desktop Right Actions */}
            <div className="hidden xl:flex items-center space-x-3">
              {/* City Language Selector - Solo en landings de ciudad */}
              {showCityLanguageLinks && (
                <CityLanguageLinks 
                  city={cityInfo} 
                  currentLanguage={cityLanguage}
                  compact={true}
                />
              )}
              <CompactLanguageSelector />
              <AuthButtons />
            </div>

            {/* Tablet Actions (hidden on mobile and desktop) */}
            <div className="hidden lg:flex xl:hidden items-center space-x-3">
              {showCityLanguageLinks && (
                <CityLanguageLinks 
                  city={cityInfo} 
                  currentLanguage={cityLanguage}
                  compact={true}
                />
              )}
              <CompactLanguageSelector />
              <AuthButtons />
            </div>
            
            {/* Mobile Actions (visible on small screens) */}
            <div className="flex lg:hidden items-center space-x-2">
              {showCityLanguageLinks && (
                <CityLanguageLinks 
                  city={cityInfo} 
                  currentLanguage={cityLanguage}
                  compact={true}
                />
              )}
            </div>
            
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="xl:hidden h-10 w-10"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
      />
    </>
  );
};

export default Header;
